import { call, takeLatest, all } from 'redux-saga/effects';
import * as api from '@api/sponsor';
import { PayloadAction } from '@reduxjs/toolkit';
import { dispatch } from '@dynamika-shared/utils/saga';
import {
  WithoutParamsKey,
  WithoutParamsDataMap,
  withoutParamsConfig,
  WithoutParamsConfigType,
  WithParamsKey,
  WithParamsDataMap,
  withParamsConfig,
  WithParamsConfigType,
} from './sponsorStatistics.config';
import { allActions } from './sponsorStatistics.slice';
import {
  SponsorStatisticsResponse,
  StatisticsParams,
} from './sponsorStatistics.types';

function* handleStatisticsFetchWithoutParams<K extends WithoutParamsKey>(
  config: WithoutParamsConfigType[K],
  action: PayloadAction<{ clinicalTrialId: string }>
): Generator {
  try {
    const response: SponsorStatisticsResponse<WithoutParamsDataMap[K]> =
      (yield call(
        api.fetchSponsorStatistics,
        action.payload.clinicalTrialId,
        config.apiType,
        {}
      )) as SponsorStatisticsResponse<WithoutParamsDataMap[K]>;
    yield dispatch(config.actions.success(response.data));
  } catch (e) {
    yield dispatch(
      config.actions.error(e instanceof Error ? e.message : 'Unknown error')
    );
  }
}

function* handleStatisticsFetchWithParams<K extends WithParamsKey>(
  config: WithParamsConfigType[K],
  action: PayloadAction<{ clinicalTrialId: string; params: StatisticsParams }>
): Generator {
  try {
    const response: SponsorStatisticsResponse<WithParamsDataMap[K]> =
      (yield call(
        api.fetchSponsorStatistics,
        action.payload.clinicalTrialId,
        config.apiType,
        action.payload.params
      )) as SponsorStatisticsResponse<WithParamsDataMap[K]>;
    yield dispatch(config.actions.success(response.data));
  } catch (e) {
    yield dispatch(
      config.actions.error(e instanceof Error ? e.message : 'Unknown error')
    );
  }
}

export function* sponsorStatisticsSaga(): Generator {
  yield all([
    takeLatest(allActions.fetchUploadsPerVisit.type, action =>
      handleStatisticsFetchWithoutParams(
        withoutParamsConfig.uploadsPerVisit,
        action as PayloadAction<{ clinicalTrialId: string }>
      )
    ),
    takeLatest(allActions.fetchQcPerStatus.type, action =>
      handleStatisticsFetchWithoutParams(
        withoutParamsConfig.qcPerStatus,
        action as PayloadAction<{ clinicalTrialId: string }>
      )
    ),
    takeLatest(allActions.fetchReadingsPerStatus.type, action =>
      handleStatisticsFetchWithoutParams(
        withoutParamsConfig.readingsPerStatus,
        action as PayloadAction<{ clinicalTrialId: string }>
      )
    ),
    takeLatest(allActions.fetchDailyQC.type, action =>
      handleStatisticsFetchWithParams(
        withParamsConfig.dailyQC,
        action as PayloadAction<{
          clinicalTrialId: string;
          params: StatisticsParams;
        }>
      )
    ),
    takeLatest(allActions.fetchDailyReads.type, action =>
      handleStatisticsFetchWithParams(
        withParamsConfig.dailyReads,
        action as PayloadAction<{
          clinicalTrialId: string;
          params: StatisticsParams;
        }>
      )
    ),
    takeLatest(allActions.fetchUploadsPerSite.type, action =>
      handleStatisticsFetchWithParams(
        withParamsConfig.uploadsPerSite,
        action as PayloadAction<{
          clinicalTrialId: string;
          params: StatisticsParams;
        }>
      )
    ),
  ]);
}
